import { isNotNil } from 'shared/src/utils/isNotNil';
import type { GoodsOption } from 'shared/src/api';
import type { OptionComponent } from 'shared/src/api/goodsService/getGoodsOptions/getGoodsOptions';

export type SelectedByDepth = { [depth: number]: OptionComponent };

export type DepthInfo = {
  lastDepth: number;
  lastComponent: OptionComponent | undefined;
  finalGoodsOption: GoodsOption | null | undefined;
  lastOptionSno: number | undefined;
  lastGoodsOptionSno: number | undefined;
  priorOptionSno: number | undefined;
  isAllSelected: boolean;
};

export const getDepthInfo = ({
  optionGroupsLength,
  selected,
}: {
  optionGroupsLength: number;
  selected: SelectedByDepth;
}): DepthInfo => {
  const lastDepth = optionGroupsLength;
  const lastComponent = selected[lastDepth];
  const finalGoodsOption = lastComponent?.goods_option;
  const isAllSelected = lastDepth > 0 && isNotNil(finalGoodsOption);
  return {
    lastDepth,
    lastComponent,
    finalGoodsOption,
    lastOptionSno: finalGoodsOption?.sno,
    lastGoodsOptionSno: lastComponent?.goods_option_sno,
    priorOptionSno:
      lastDepth > 1 ? selected[lastDepth - 1]?.goods_option_sno : undefined,
    isAllSelected,
  };
};

export type RestockState = {
  isSoldOut: boolean;
  isRestockMode: boolean;
  isAlreadySubscribed: boolean;
};

export const getRestockState = ({
  finalGoodsOption,
  isAllSelected,
}: {
  finalGoodsOption: GoodsOption | null | undefined;
  isAllSelected: boolean;
}): RestockState => {
  const isSoldOut = finalGoodsOption?.is_soldout ?? false;
  const canApplyRestock =
    finalGoodsOption?.is_possible_applying_restock_noti ?? false;
  return {
    isSoldOut,
    isRestockMode: isAllSelected && isSoldOut && canApplyRestock,
    isAlreadySubscribed: finalGoodsOption?.is_applied_for_restock === true,
  };
};

export type AvailableCount = {
  maxAvailableCount: number;
  stepperMaxCount: number;
};

export const getAvailableCount = ({
  finalGoodsOption,
  isAllSelected,
}: {
  finalGoodsOption: GoodsOption | null | undefined;
  isAllSelected: boolean;
}): AvailableCount => {
  if (!isAllSelected || !finalGoodsOption) {
    return { maxAvailableCount: 0, stepperMaxCount: 1 };
  }
  const alreadyPurchasedCount = finalGoodsOption.purchase_count ?? 0;
  const maxOrderLimitCount = finalGoodsOption.max_order_limit_count;
  const maxAvailableCount = Math.max(
    0,
    isNotNil(maxOrderLimitCount) && maxOrderLimitCount > 0
      ? Math.min(
          finalGoodsOption.stock,
          maxOrderLimitCount - alreadyPurchasedCount,
        )
      : finalGoodsOption.stock,
  );
  return {
    maxAvailableCount,
    stepperMaxCount: Math.max(1, maxAvailableCount),
  };
};

export type InlineOptionState = DepthInfo &
  RestockState &
  AvailableCount & {
    isPurchasable: boolean;
  };

export const computeInlineOptionState = ({
  optionGroupsLength,
  selected,
  count,
}: {
  optionGroupsLength: number;
  selected: SelectedByDepth;
  count: number;
}): InlineOptionState => {
  const depth = getDepthInfo({ optionGroupsLength, selected });
  const restock = getRestockState({
    finalGoodsOption: depth.finalGoodsOption,
    isAllSelected: depth.isAllSelected,
  });
  const available = getAvailableCount({
    finalGoodsOption: depth.finalGoodsOption,
    isAllSelected: depth.isAllSelected,
  });
  const isPurchasable =
    depth.isAllSelected &&
    !restock.isSoldOut &&
    available.maxAvailableCount > 0 &&
    count <= available.stepperMaxCount;

  return {
    ...depth,
    ...restock,
    ...available,
    isPurchasable,
  };
};
